import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  DialogContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  TextField,
  Autocomplete,
  CircularProgress,
  Button
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import DialogTransition from 'src/components/DialogTransition';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  LOAD_AVAILABLE_EQUIPMENTS,
  ASSIGN_EQUIPMENTS_TO_INSURANCE,
  loadAvailableEquipments,
  assignEquipmentsToInsuranceAction
} from 'src/store/actions/equipmentsActions';

import { selectAvailableEquipments } from 'src/store/selectors/equipmentSelectors';
import Dialog from 'src/components/Dialog/Dialog';
import { getEquipmentDisplayName } from 'src/utils/constants/insurance';
import makeInitialState from './initialState';
import validationSchema from './schema';

const AssociateEquipmentDialog = ({ open, onClose, policyId }) => {
  const dispatch = useDispatch();
  const availableEquipments = useSelector(selectAvailableEquipments);
  const isLoadingAvailableEquipments = useSelector(
    isLoadingSelector([LOAD_AVAILABLE_EQUIPMENTS.action])
  );
  const isAssigning = useSelector(
    isLoadingSelector([ASSIGN_EQUIPMENTS_TO_INSURANCE.action])
  );
  const initialState = makeInitialState();

  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch(loadAvailableEquipments());
    }
  }, [open, dispatch]);

  const handleAssignEquipments = values => {
    dispatch(
      assignEquipmentsToInsuranceAction(
        {
          policyId,
          equipmentsIds: values.selectedEquipments.map(
            equipment => equipment.id
          )
        },
        onClose
      )
    );
  };

  if (!open) return null;

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: 'md'
        }
      }}
      open={open}
      handleClose={onClose}
      TransitionComponent={DialogTransition}
      title={t('INSURANCE_VIEW.ASSOCIATE_EQUIPMENTS')}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        <Formik
          initialValues={initialState}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleAssignEquipments}
        >
          {({ errors, handleSubmit, touched, values, setFieldValue }) => {
            const handleAddEquipment = (event, newValue) => {
              if (
                newValue &&
                !values.selectedEquipments.some(
                  equipment => equipment.id === newValue.id
                )
              ) {
                const newSelectedEquipments = [
                  ...values.selectedEquipments,
                  newValue
                ];
                setFieldValue('selectedEquipments', newSelectedEquipments);
              }
            };

            const handleRemoveEquipment = equipmentId => {
              const newSelectedEquipments = values.selectedEquipments.filter(
                equipment => equipment.id !== equipmentId
              );
              setFieldValue('selectedEquipments', newSelectedEquipments);
            };

            const isEquipmentSelected = equipmentId => {
              return values.selectedEquipments.some(
                equipment => equipment.id === equipmentId
              );
            };

            const availableEquipmentsFiltered = availableEquipments.filter(
              equipment => !isEquipmentSelected(equipment.id)
            );

            return (
              <Form onSubmit={handleSubmit}>
                <Box mb={3} sx={{ width: '100%' }}>
                  <Typography variant="h6" marginBottom={2} gutterBottom>
                    {t('INSURANCE_VIEW.SELECT_EQUIPMENTS')}
                  </Typography>

                  <Autocomplete
                    options={availableEquipmentsFiltered}
                    getOptionLabel={getEquipmentDisplayName}
                    onChange={handleAddEquipment}
                    loading={isLoadingAvailableEquipments}
                    clearOnBlur
                    selectOnFocus={false}
                    handleHomeEndKeys={false}
                    freeSolo={false}
                    ListboxProps={{
                      'data-testid': 'equipment-options-listbox'
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t('INSURANCE_VIEW.SEARCH_EQUIPMENTS')}
                        variant="outlined"
                        fullWidth
                        placeholder={t(
                          'INSURANCE_VIEW.TYPE_TO_SEARCH_EQUIPMENTS'
                        )}
                        inputProps={{
                          ...params.inputProps,
                          'data-testid': 'equipment-options-input'
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        data-testid="equipment-option"
                      >
                        <Typography variant="body1">
                          {getEquipmentDisplayName(option)}
                        </Typography>
                      </Box>
                    )}
                    noOptionsText={t('INSURANCE_VIEW.NO_EQUIPMENTS_FOUND')}
                    loadingText={t('COMMON.LOADING')}
                  />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Box mb={3}>
                  <Typography variant="h6" gutterBottom marginBottom={2}>
                    {t('INSURANCE_VIEW.SELECTED_EQUIPMENTS').replace(
                      '{number}',
                      values.selectedEquipments.length
                    )}
                  </Typography>
                  {values.selectedEquipments.length > 0 ? (
                    <List dense>
                      {values.selectedEquipments.map(equipment => (
                        <ListItem key={equipment.id}>
                          <ListItemText
                            primary={getEquipmentDisplayName(equipment)}
                            secondary={`${t('COMMON.ID')}: ${equipment.id}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() =>
                                handleRemoveEquipment(equipment.id)
                              }
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {t('INSURANCE_VIEW.NO_EQUIPMENTS_SELECTED')}
                    </Typography>
                  )}
                  {touched.selectedEquipments && errors.selectedEquipments && (
                    <Typography variant="body2" color="error" mt={1}>
                      {t(errors.selectedEquipments)}
                    </Typography>
                  )}
                </Box>

                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{ width: '100%' }}
                    disabled={isAssigning}
                  >
                    {t('COMMON.CANCEL')}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ width: '100%' }}
                    color="primary"
                    type="submit"
                    loading={isAssigning}
                    disabled={
                      isAssigning || values.selectedEquipments.length === 0
                    }
                  >
                    {isAssigning ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      t('COMMON.ASSIGN')
                    )}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

AssociateEquipmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  policyId: PropTypes.string.isRequired
};

export default AssociateEquipmentDialog;
